/**
 * LeadSidePanel — quick operational preview for the LeadsBoard.
 *
 * Jerarquía CRM: lista = escanear · preview = actuar rápido · ficha
 * completa (/leads/:id) = entender y editar todo. El panel NO duplica la
 * ficha; contiene sólo lo operativo:
 *   - Cabecera compacta: fase + señales, teléfono, país, fuente, acceso
 *     claro a la ficha completa (header + footer sticky).
 *   - Alertas contextuales (sólo si aplican): reunión hoy/próxima,
 *     ventana WhatsApp cerrada, villa en hold. El follow-up vencido ya
 *     llega como signal chip del backend (overdue_action).
 *   - Resumen comercial mínimo: interés, presupuesto, temperatura.
 *   - Cambio de fase rápido: LeadPipelineStepper compact (mismo PATCH
 *     /leads/:id que la ficha).
 *   - Próxima acción editable (mismo PATCH).
 *   - Reunión: sólo estado + enlace a la ficha (sin formularios).
 *   - WhatsApp operativo: LeadConversation REUTILIZADO tal cual (mismas
 *     reglas de 24h, idempotencia, media y estados; admin-only V1, igual
 *     que en la ficha). key={lead_id} fuerza remount limpio al cambiar
 *     de lead — cero drafts o mensajes heredados.
 * Todo lo demás (perfil del comprador, canales, dossier, villa hold,
 * checklist, plantillas standalone, notas, timeline) vive en la ficha.
 *
 * BUGFIX heredado: backend PATCH /leads/{id} devuelve {ok:true}, no el
 * lead — tras cada mutación se re-lee GET /leads/{id} para mantener el
 * estado canónico.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowSquareOut, Calendar, EnvelopeSimple, Globe, House, MapPin, Phone,
  Warning, WhatsappLogo,
} from "@phosphor-icons/react";

import SidePanel from "@/components/ui-dv/SidePanel";
import Chip from "@/components/ui-dv/Chip";
import LeadPipelineStepper from "@/components/leads/LeadPipelineStepper";
import LeadConversation from "@/components/leads/LeadConversation";
import { sourceLabel } from "@/components/leads/LeadContactSummary";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { LEAD_STATUS_MAP, normalizeStatus, SIGNAL_META } from "@/lib/leadStatuses";
import { landingLabel } from "@/lib/leadLanding";
import { leadWhatsappHref } from "@/lib/bulkWhatsapp";
import { relativeActivity, relativeDueDate } from "@/lib/relativeDate";

const WINDOW_MS = 24 * 60 * 60 * 1000;

const VILLA_MODEL_LABEL = {
  dolce_vistas: "Dolce Vistas",
  dolce_prive:  "Dolce Privè",
  either:       "Cualquiera",
};

const TEMPERATURE_META = {
  hot:  { label: "Hot",  cls: "bg-red-50 border-red-200 text-red-700" },
  warm: { label: "Warm", cls: "bg-amber-50 border-amber-200 text-amber-800" },
  cold: { label: "Cold", cls: "bg-sky-50 border-sky-200 text-sky-700" },
};

const MEETING_STATE_META = {
  scheduled: { label: "Programada", tone: "cyan" },
  done:      { label: "Realizada",  tone: "emerald" },
  cancelled: { label: "Cancelada",  tone: "stone" },
};

function statusTone(key) {
  switch (key) {
    case "new":                return "slate";
    case "contacted":          return "blue";
    case "meeting_scheduled":  return "cyan";
    case "dossier":            return "violet";
    case "in_followup":        return "amber";
    case "reserved":           return "emerald";
    case "lost":               return "stone";
    default:                   return "stone";
  }
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("es-ES", {
      day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

export default function LeadSidePanel({
  leadId,
  open,
  onClose,
  onChanged,        // (lead) => void  — board refresh after any patch
}) {
  const { user } = useAuth();
  // Rol REAL (no effectiveRole): la conversación WhatsApp es admin-only
  // en V1 y el backend aplica el mismo guard.
  const isAdmin = user?.role === "admin";
  const [lead, setLead] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [retryTick, setRetryTick] = useState(0);
  // Alerta "villa en hold" — lectura ligera best-effort del endpoint
  // existente; sin alerta si falla o no hay holds activos.
  const [activeHolds, setActiveHolds] = useState(0);

  // Local editable state for próxima acción
  const [actText, setActText] = useState("");
  const [actDate, setActDate] = useState("");
  const [actTime, setActTime] = useState("");

  // Refetch the canonical lead by stable id. Used both by the initial load
  // (when leadId/open change) and by retryTick bumps.
  useEffect(() => {
    if (!open || !leadId) return;
    let cancelled = false;
    // Only clear the previous lead when the selected id actually changes.
    // For in-place refreshes (retryTick), keep the existing lead visible to
    // avoid a flash of "Cargando…" between edits.
    setLead((prev) => (prev && prev.lead_id === leadId ? prev : null));
    setError("");
    api.get(`/leads/${leadId}`, { timeout: 8000 })
      .then(r => {
        if (cancelled) return;
        setLead(r.data);
        setActText(r.data.next_action || "");
        setActDate((r.data.next_action_date || "").slice(0, 10));
        setActTime((r.data.next_action_time || "").slice(0, 5));
      })
      .catch(e => !cancelled && setError(formatError(e)));
    return () => { cancelled = true; };
  }, [leadId, open, retryTick]);

  useEffect(() => {
    if (!open || !leadId) return;
    let cancelled = false;
    setActiveHolds(0);
    api.get(`/villa-holds?lead_id=${encodeURIComponent(leadId)}`)
      .then(r => {
        if (cancelled) return;
        const rows = Array.isArray(r.data) ? r.data : [];
        setActiveHolds(rows.filter(h => h.status === "active").length);
      })
      .catch(() => { /* best-effort: la alerta simplemente no aparece */ });
    return () => { cancelled = true; };
  }, [leadId, open, retryTick]);

  const patch = async (payload) => {
    if (!leadId) return;
    setBusy(true); setError("");
    try {
      await api.patch(`/leads/${leadId}`, payload);
      const { data } = await api.get(`/leads/${leadId}`);
      setLead(data);
      onChanged?.(data);
    } catch (e) { setError(formatError(e)); }
    finally { setBusy(false); }
  };

  const statusKey = lead ? normalizeStatus(lead.status) : null;
  const status = statusKey ? LEAD_STATUS_MAP[statusKey] : null;
  const signals = lead?.signals || [];
  const due = relativeDueDate(lead?.next_action_date);

  // Ventana 24h cerrada — sólo alerta si alguna vez hubo inbound (si no,
  // sería ruido en leads sin conversación). Heurística de display; el
  // backend sigue siendo la autoridad al enviar.
  const waWindowClosed = useMemo(() => {
    if (!isAdmin || !lead?.last_wa_inbound_at) return false;
    const t = Date.parse(lead.last_wa_inbound_at);
    return Number.isFinite(t) && Date.now() - t >= WINDOW_MS;
  }, [isAdmin, lead]);

  // Reunión hoy / próxima (<48h), sólo si no está realizada/cancelada.
  const meetingAlert = useMemo(() => {
    if (!lead?.meeting_at) return null;
    if (["done", "cancelled"].includes(lead.meeting_state)) return null;
    const t = Date.parse(lead.meeting_at);
    if (!Number.isFinite(t)) return null;
    if (new Date(t).toDateString() === new Date().toDateString()) return "Reunión hoy";
    const diff = t - Date.now();
    if (diff > 0 && diff < 48 * 60 * 60 * 1000) return "Reunión próxima";
    return null;
  }, [lead]);

  const interestText =
    (lead?.interest || "").trim() ||
    VILLA_MODEL_LABEL[lead?.villa_model_preference] ||
    "";
  const temp = lead?.lead_temperature ? TEMPERATURE_META[lead.lead_temperature] : null;
  const meetingState = lead?.meeting_state ? MEETING_STATE_META[lead.meeting_state] : null;
  // Mismo teléfono canónico y misma normalización que la ficha completa.
  const waHref = leadWhatsappHref(lead);

  return (
    <SidePanel
      open={open}
      onClose={onClose}
      title={lead?.name || "Lead"}
      subtitle="Lead"
      testId="lead-side-panel"
      headerRight={
        leadId && (
          <>
            {/* Atajo de WhatsApp: el trabajo más frecuente del panel, sin
                bajar hasta la conversación (que además es admin-only).
                Solo aparece si el lead tiene un número usable — un botón
                que parece funcionar y no funciona es peor que ninguno. */}
            {waHref && (
              <a
                href={waHref}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="lead-side-whatsapp"
                aria-label={`Abrir WhatsApp con ${lead?.name || "el lead"}`}
                title="Abrir WhatsApp"
                className="min-h-[44px] min-w-[44px] sm:min-h-0 sm:min-w-0 sm:px-2.5 sm:py-1.5 px-3 rounded border border-emerald-300 text-emerald-700 hover:border-emerald-700 hover:bg-emerald-50 inline-flex items-center justify-center gap-1 whitespace-nowrap text-xs"
              >
                <WhatsappLogo size={15} />
                <span className="hidden sm:inline">WhatsApp</span>
              </a>
            )}
            {/* En el móvil solo el icono: el atajo de WhatsApp ocupa sitio
                en una cabecera estrecha y el nombre del lead se cortaba.
                El texto completo sigue en el pie ("Abrir ficha completa"),
                así que no se pierde ninguna salida hacia la ficha. */}
            <Link
              to={`/leads/${leadId}`}
              data-testid="lead-side-open-full-header"
              aria-label="Abrir ficha completa"
              title="Ficha completa"
              className="text-xs min-h-[44px] min-w-[44px] sm:min-h-0 sm:min-w-0 px-3 sm:px-2.5 sm:py-1.5 rounded border border-stone-300 text-stone-700 hover:border-stone-900 hover:text-stone-900 inline-flex items-center justify-center gap-1 whitespace-nowrap"
            >
              <ArrowSquareOut size={13} />
              <span className="hidden sm:inline">Ficha completa</span>
            </Link>
          </>
        )
      }
      footer={
        leadId && (
          <Link
            to={`/leads/${leadId}`}
            data-testid="lead-side-panel-open-full"
            className="inline-flex items-center gap-1.5 text-sm text-stone-700 hover:text-stone-900"
          >
            <ArrowSquareOut size={14} /> Abrir ficha completa
          </Link>
        )
      }
    >
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 text-sm rounded p-3 flex items-center justify-between gap-3">
          <span className="truncate">{error}</span>
          <button
            onClick={() => setRetryTick(t => t + 1)}
            data-testid="lead-side-panel-retry"
            className="shrink-0 text-xs px-2 py-1 rounded bg-white border border-red-200 text-red-700 hover:bg-red-100"
          >
            Reintentar
          </button>
        </div>
      )}

      {!lead && !error && <div className="text-stone-400 text-sm">Cargando…</div>}

      {lead && (
        <>
          {/* Fase + señales + alertas contextuales — una sola fila de
              chips: cero ruido cuando no aplica ninguna. */}
          <div className="flex flex-wrap items-center gap-1.5" data-testid="lead-side-chips">
            {status && (
              <Chip tone={statusTone(statusKey)} dot dotColor={status.dotColor} size="sm">
                {status.label}
              </Chip>
            )}
            {signals.map(s => (
              <Chip key={s} tone={SIGNAL_META[s]?.tone === "red" ? "red" : "amber"} size="sm">
                {SIGNAL_META[s]?.label || s}
              </Chip>
            ))}
            {meetingAlert && (
              <Chip tone="cyan" size="sm" icon={<Calendar size={10} />} data-testid="lead-side-alert-meeting">
                {meetingAlert}
              </Chip>
            )}
            {waWindowClosed && (
              <Chip tone="stone" size="sm" icon={<WhatsappLogo size={10} />} data-testid="lead-side-alert-wa-window">
                Ventana 24h cerrada
              </Chip>
            )}
            {activeHolds > 0 && (
              <Chip tone="violet" size="sm" icon={<House size={10} />} data-testid="lead-side-alert-hold">
                Villa en hold
              </Chip>
            )}
            <span className="text-[11px] text-stone-400 ml-auto">
              Última actividad {relativeActivity(lead.updated_at || lead.created_at) || "—"}
            </span>
          </div>

          {/* Cabecera compacta — datos de contacto mínimos, sin edición
              (editar contacto → ficha completa). */}
          <div className="grid grid-cols-2 gap-x-3 gap-y-2" data-testid="lead-side-contact-compact">
            <InfoCell icon={Phone} label="Teléfono / WhatsApp">
              {lead.phone
                ? <a href={`tel:${lead.phone}`} className="hover:underline">{lead.phone}</a>
                : null}
            </InfoCell>
            <InfoCell icon={MapPin} label="País">{lead.country}</InfoCell>
            <InfoCell icon={Globe} label="Fuente">{sourceLabel(lead.source)}</InfoCell>
            <InfoCell label="Interés">{interestText}</InfoCell>
          </div>

          {/* Resumen comercial mínimo */}
          <div className="grid grid-cols-2 gap-x-3 gap-y-2" data-testid="lead-side-summary">
            <InfoCell label="Presupuesto">
              {lead.budget_eur
                ? `€${Number(lead.budget_eur).toLocaleString("es-ES")}`
                : null}
            </InfoCell>
            <InfoCell label="Temperatura">
              {temp && (
                <span className={`inline-flex text-[11px] px-2 py-0.5 rounded-full border ${temp.cls}`}>
                  {temp.label}
                </span>
              )}
            </InfoCell>
          </div>

          {/* Cambio de fase rápido — mismo componente y PATCH que la ficha. */}
          <Section label="Estado">
            <LeadPipelineStepper
              status={lead.status}
              onChange={(nextStatus) => patch({ status: nextStatus })}
              compact
              disabled={busy}
            />
          </Section>

          {/* Próxima acción — editor compacto existente (mismo PATCH). */}
          <Section label="Próxima acción">
            <div className="space-y-2">
              <input
                type="text"
                value={actText}
                onChange={e => setActText(e.target.value)}
                placeholder="¿Qué hay que hacer?"
                className="w-full text-sm border border-stone-200 rounded px-2 py-1.5"
                data-testid="lead-side-action-text"
              />
              <div className="flex items-center gap-2 flex-wrap">
                <input
                  type="date"
                  value={actDate}
                  onChange={e => setActDate(e.target.value)}
                  className="text-sm border border-stone-200 rounded px-2 py-1.5"
                  data-testid="lead-side-action-date"
                />
                <input
                  type="time"
                  value={actTime}
                  onChange={e => setActTime(e.target.value)}
                  disabled={!actDate}
                  title={actDate ? "" : "Define una fecha para añadir hora"}
                  className="text-sm border border-stone-200 rounded px-2 py-1.5 disabled:opacity-40"
                  data-testid="lead-side-action-time"
                />
                {due && (
                  <Chip size="xs" tone={due.tone}>
                    {due.text}{lead.next_action_time ? ` · ${lead.next_action_time}` : ""}
                  </Chip>
                )}
                <button
                  onClick={() => patch({
                    next_action: actText.trim() || null,
                    next_action_date: actDate || null,
                    next_action_time: actDate ? (actTime || null) : null,
                  })}
                  disabled={busy}
                  data-testid="lead-side-action-save"
                  className="ml-auto text-xs px-2.5 py-1 rounded bg-stone-900 text-white hover:bg-stone-800 disabled:opacity-50"
                >
                  Guardar
                </button>
              </div>
            </div>
          </Section>

          {/* Reunión — sólo operativa: estado + acceso; la gestión
              completa (dossier incluido) vive en la ficha. */}
          <Section label="Reunión">
            <div className="flex items-center justify-between gap-2 text-sm" data-testid="lead-side-meeting">
              <span className="inline-flex items-center gap-1.5 text-stone-700 min-w-0">
                <Calendar size={13} className="text-stone-400 shrink-0" />
                {lead.meeting_at ? (
                  <>
                    <span className="truncate">{fmtDate(lead.meeting_at)}</span>
                    {meetingState && (
                      <Chip size="xs" tone={meetingState.tone}>{meetingState.label}</Chip>
                    )}
                  </>
                ) : (
                  <span className="text-stone-400 italic">Sin reunión programada</span>
                )}
              </span>
              <Link
                to={`/leads/${leadId}`}
                className="shrink-0 text-xs text-stone-600 hover:text-stone-900 underline"
                data-testid="lead-side-meeting-manage"
              >
                Gestionar
              </Link>
            </div>
          </Section>

          {/* WhatsApp operativo — el MISMO componente que la ficha
              completa (ventana 24h, idempotencia, media, ticks, retry).
              Admin-only V1 (rol real; backend aplica el mismo guard).
              key fuerza remount limpio al cambiar de lead. */}
          {isAdmin && (
            <Section label="WhatsApp">
              <LeadConversation
                key={lead.lead_id}
                leadId={lead.lead_id}
                lead={lead}
                unreadCount={lead.wa_unread_count || 0}
                lastInboundAt={lead.last_wa_inbound_at || null}
              />
            </Section>
          )}

          {/* Bloques condicionales heredados — información que SÓLO
              existe aquí (la ficha no la muestra; LeadCard sólo lleva
              badges). Se conservan para no perder funcionalidad; al ser
              condicionales no añaden ruido al preview normal. */}
          {lead.referred_by_investor_id && (
            <Section label="Referido por">
              <div
                className="rounded-md border px-3 py-2.5 text-sm"
                style={{ borderColor: "#e4cdbf", background: "#fbf3ee" }}
                data-testid="lead-side-referred-by"
              >
                <div className="font-medium text-stone-800">
                  {lead.referred_by_name || lead.referred_by_email || "Inversor"}
                </div>
                {lead.referred_by_email && (
                  <div className="text-xs text-stone-600">
                    <a href={`mailto:${lead.referred_by_email}`} className="hover:underline">
                      {lead.referred_by_email}
                    </a>
                  </div>
                )}
              </div>
            </Section>
          )}

          {(lead.duplicate_submissions_count || 0) > 0 && (
            <Section label="Reenvíos del formulario">
              <DuplicateSubmissions lead={lead} />
            </Section>
          )}

          {lead.initial_email && (
            <Section label="Automatizaciones">
              <InitialEmailRow ie={lead.initial_email} />
            </Section>
          )}

          {isAdmin && (
            <AdminLeadActions
              lead={lead}
              onArchived={() => { onChanged?.(lead); onClose?.(); }}
              onDeleted={() => { onChanged?.(lead); onClose?.(); }}
              onUnarchived={(fresh) => setLead(fresh)}
            />
          )}
        </>
      )}
    </SidePanel>
  );
}

function DuplicateSubmissions({ lead }) {
  const count = lead.duplicate_submissions_count || 0;
  const all = lead.duplicate_submissions || [];
  const recent = all.slice(-5).reverse();
  const latest = recent[0];
  const matchedLabel = (m) => (m === "phone" ? "coincidió por teléfono" : "coincidió por email");
  return (
    <div className="space-y-2" data-testid="lead-side-duplicates">
      <div className="text-sm text-stone-700">
        Este lead volvió a rellenar el formulario <strong>{count}</strong> {count === 1 ? "vez" : "veces"}.
      </div>
      {latest && (
        <div className="text-xs text-stone-500">
          Último: {fmtDate(latest.submitted_at)}
          {" · "}{landingLabel(latest.landing_source)}
          {" · "}{matchedLabel(latest.matched_by)}
        </div>
      )}
      {recent.length > 0 && (
        <ul className="space-y-1.5 max-h-48 overflow-y-auto pr-1">
          {recent.map((d, i) => (
            <li
              key={`${d.submitted_at}-${i}`}
              className="text-xs bg-stone-50 border border-stone-200 rounded p-2"
            >
              <div className="text-stone-600">
                {fmtDate(d.submitted_at)}
                {" · "}{landingLabel(d.landing_source)}
                {" · "}{matchedLabel(d.matched_by)}
              </div>
              {(d.interest || d.campaign) && (
                <div className="text-[11px] text-stone-500 mt-0.5">
                  {d.interest && <>Interés: {d.interest}</>}
                  {d.interest && d.campaign && " · "}
                  {d.campaign && <>Campaña: {d.campaign}</>}
                </div>
              )}
              {d.message_preview && (
                <div className="text-[11px] text-stone-700 mt-1 whitespace-pre-wrap">
                  “{d.message_preview}”
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function InitialEmailRow({ ie }) {
  const isOk = ie.status === "ok";
  const rel = relativeActivity(ie.sent_at);
  const baseClass = isOk
    ? "border-cyan-200 bg-cyan-50/40 text-cyan-900"
    : "border-red-200 bg-red-50/50 text-red-900";
  return (
    <div className={`rounded border ${baseClass} p-2.5 space-y-1`} data-testid="lead-side-initial-email">
      <div className="flex items-center gap-2 text-sm">
        <span className="shrink-0 inline-flex items-center justify-center w-5 h-5 rounded-full bg-white/70">
          {isOk
            ? <EnvelopeSimple size={11} weight="bold" />
            : <Warning size={11} weight="bold" />}
        </span>
        <span className="font-medium">
          Email inicial · {isOk ? "OK" : "Falló"}
          {rel && <span className="text-stone-500 font-normal"> · {rel}</span>}
        </span>
      </div>
      {(ie.subject || ie.template) && (
        <div className="text-xs text-stone-600 pl-7 truncate" title={ie.subject || ""}>
          {ie.subject ? `"${ie.subject}"` : ""}
          {ie.subject && ie.template ? " · " : ""}
          {ie.template ? <span className="font-mono text-[10px]">{ie.template}</span> : null}
        </div>
      )}
      {!isOk && ie.error && (
        <div className="text-xs text-red-700 pl-7" title={ie.error}>
          {ie.error.length > 90 ? ie.error.slice(0, 90) + "…" : ie.error}
        </div>
      )}
    </div>
  );
}

function Section({ label, children }) {
  return (
    <section>
      <div className="text-[10px] uppercase tracking-widest text-stone-500 mb-1.5">{label}</div>
      {children}
    </section>
  );
}

function InfoCell({ icon: Icon, label, children }) {
  const empty = children == null || children === "";
  return (
    <div className="min-w-0">
      <div className="text-[10px] uppercase tracking-widest text-stone-400 inline-flex items-center gap-1">
        {Icon && <Icon size={10} />} {label}
      </div>
      {empty ? (
        <div className="text-xs text-stone-400 italic">—</div>
      ) : (
        <div className="text-sm text-stone-800 truncate">{children}</div>
      )}
    </div>
  );
}

/**
 * Admin-only footer with Archive / Unarchive / Hard-Delete actions.
 * Hard-delete triggers an in-panel confirmation instead of window.confirm
 * so the destructive flow stays premium.
 */
function AdminLeadActions({ lead, onArchived, onUnarchived, onDeleted }) {
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [err, setErr] = useState("");

  const archive = async () => {
    setBusy(true); setErr("");
    try {
      await api.post(`/leads/${lead.lead_id}/archive`);
      onArchived?.();
    } catch (e) { setErr(formatError(e)); }
    finally { setBusy(false); }
  };
  const unarchive = async () => {
    setBusy(true); setErr("");
    try {
      await api.post(`/leads/${lead.lead_id}/unarchive`);
      const fresh = (await api.get(`/leads/${lead.lead_id}`)).data;
      onUnarchived?.(fresh);
    } catch (e) { setErr(formatError(e)); }
    finally { setBusy(false); }
  };
  const hardDelete = async () => {
    setBusy(true); setErr("");
    try {
      await api.delete(`/leads/${lead.lead_id}?confirm=true`);
      onDeleted?.();
    } catch (e) { setErr(formatError(e)); }
    finally { setBusy(false); }
  };

  return (
    <section
      data-testid="lead-admin-actions"
      className="mt-6 pt-4 border-t border-dashed border-stone-300 space-y-2"
    >
      <div className="text-[10px] uppercase tracking-widest text-stone-500">
        Acciones de administrador
      </div>

      {err && (
        <div className="bg-red-50 border border-red-200 text-red-800 text-xs rounded p-2">{err}</div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        {lead.archived ? (
          <button
            onClick={unarchive}
            disabled={busy}
            data-testid="lead-unarchive-btn"
            className="text-xs px-3 py-1.5 rounded border border-stone-300 hover:border-stone-900 text-stone-700 hover:text-stone-900 disabled:opacity-50"
          >
            Desarchivar
          </button>
        ) : (
          <button
            onClick={archive}
            disabled={busy}
            data-testid="lead-archive-btn"
            className="text-xs px-3 py-1.5 rounded border border-stone-300 hover:border-stone-900 text-stone-700 hover:text-stone-900 disabled:opacity-50"
          >
            Archivar
          </button>
        )}

        {!confirmDelete ? (
          <button
            onClick={() => setConfirmDelete(true)}
            data-testid="lead-delete-btn"
            className="text-xs px-3 py-1.5 rounded border border-red-300 text-red-700 hover:bg-red-50"
          >
            Eliminar definitivamente
          </button>
        ) : (
          <div
            data-testid="lead-delete-confirm"
            className="flex items-center gap-2 bg-red-50 border border-red-200 rounded px-2.5 py-1.5 text-xs text-red-900"
          >
            <span>¿Seguro? Borra lead + tasks + automations.</span>
            <button
              onClick={hardDelete}
              disabled={busy}
              data-testid="lead-delete-confirm-yes"
              className="px-2 py-0.5 rounded bg-red-700 text-white hover:bg-red-800 disabled:opacity-50"
            >
              Sí, eliminar
            </button>
            <button
              onClick={() => setConfirmDelete(false)}
              disabled={busy}
              data-testid="lead-delete-confirm-no"
              className="px-2 py-0.5 rounded border border-red-300 text-red-700 hover:bg-red-100"
            >
              Cancelar
            </button>
          </div>
        )}
      </div>

      <div className="text-[11px] text-stone-500">
        Archivar oculta el lead del Kanban pero lo mantiene en la base. Eliminar es irreversible.
      </div>
    </section>
  );
}
